<template>
  <div class="panel-container is-animated slide-from-right">
    <header class="panel-header">
      <div class="panel-left-item">
        <button
          class="text-button narrow-button"
          data-testid="settingsSharePanelPrivacyBackButton"
          @click="goBackToMainPanel"
        >
          {{ areBaseWallAccessSettingsDirty ? __('Cancel') : __('Back') }}
        </button>
      </div>
      <h3>{{ __('Change privacy') }}</h3>
      <div class="panel-right-item">
        <button
          v-if="areBaseWallAccessSettingsDirty"
          key="save"
          class="flat-button narrow-button"
          data-testid="settingsSharePanelSavePrivacyButton"
          @click="save"
        >
          {{ __('Save') }}
        </button>
        <button
          v-else
          key="saved"
          class="text-button narrow-button"
          data-testid="settingsSharePanelSavedPrivacyButton"
          disabled
          :title="__('The padlet is up to date')"
        >
          {{ __('Saved') }}
        </button>
      </div>
    </header>

    <div class="panel-status-bar">
      <progress-indeterminate v-if="isSaving" />
      <div v-if="errorMessage" class="alert alert-error" v-text="errorMessage" />
    </div>

    <div class="panel-body">
      <section>
        <div class="section-body">
          <div class="action-list list line-separated">
            <div
              v-for="option in privacyOptions"
              :key="option.type"
              :class="['list-item privacy-option', !option.isEnabled && 'is-disabled']"
              :disabled="!option.isEnabled"
              :data-testid="`${sanitizeAndCamelCaseTitle(option.type)}PrivacyOption`"
              @click="selectPrivacyType(option.type)"
            >
              <div class="list-item-primary">
                <base-icon
                  :class="{ 'is-highlighted': isSelected(option.type) }"
                  :name="WALL_ACCESS_PRIVACY_TYPE_ICON[option.type]"
                />
                <div class="list-item-text">
                  <div>{{ privacyTypeShortName[option.type] }}</div>
                  <div class="small-caption-type description">{{ privacyText[option.type] }}</div>
                </div>
              </div>
              <div class="list-item-secondary">
                <base-icon v-if="!isSelected(option.type)" name="radio_button_unchecked" />
                <base-icon v-else class="is-highlighted" name="radio_button_checked" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div class="section-body">
          <div v-if="xOptions" class="section-list list privacy-option-settings">
            <div v-if="xLoggedInVisitorsToggle" class="list-item form-group password-input">
              <p id="privacy-logged-in-visitors" class="list-item-primary">
                {{ __('Logged in visitors only') }}
              </p>
              <OzToggle
                aria-labelledby="privacy-logged-in-visitors"
                :dark-mode="false"
                :value="isPrivacyOptionMembersOnly"
                :disabled="
                  (isPrivacyOptionSecretLoggedInVisitors && !canITurnOffSecretLoggedInVisitorsOnlyToggle) ||
                  (isPrivacyOptionSecret && !canITurnOnSecretLoggedInVisitorsOnlyToggle)
                "
                data-testid="loggedInVisitorsOnlyToggle"
                @input="toggleLoggedInVisitorsOnly"
              />
            </div>
            <div v-if="xDisplayInLibraryDashboardToggle" class="list-item form-group password-input">
              <p id="privacy-display-in-team-dashboard" class="list-item-primary">
                {{ displayInLibraryDashboardToggleText }}
              </p>
              <OzToggle
                aria-labelledby="privacy-display-in-team-dashboard"
                :dark-mode="false"
                :value="isPrivacyOptionLibraryWideListed"
                :disabled="
                  (isPrivacyOptionLibraryWideListed && !canITurnOffDisplayInLibraryDashboardToggle) ||
                  (isPrivacyOptionLibraryWideUnlisted && !canITurnOnDisplayInLibraryDashboardToggle)
                "
                data-testid="displayInLibraryDashboardToggle"
                @input="toggleDisplayInTeamDashboard"
              />
            </div>
            <div v-if="xDisplayInOrgDashboardToggle" class="list-item form-group password-input">
              <p id="privacy-display-in-org-dashboard" class="list-item-primary">
                {{ __('Display in org dashboard') }}
              </p>
              <OzToggle
                aria-labelledby="privacy-display-in-org-dashboard"
                :dark-mode="false"
                :value="isPrivacyOptionOrgWideListed"
                :disabled="
                  (isPrivacyOptionOrgWideListed && !canITurnOffDisplayInOrgDashboardToggle) ||
                  (!isPrivacyOptionOrgWideListed && !canITurnOnDisplayInOrgDashboardToggle)
                "
                data-testid="displayInOrgDashboardToggle"
                @input="toggleDisplayInOrgDashboard"
              />
            </div>
            <div v-if="xPassword" class="list-item form-group password-input">
              <label for="privacy-password" class="list-item-primary">{{ __('Password') }}</label>
              <input
                id="privacy-password"
                type="text"
                class="list-item-secondary privacy-password"
                :value="password"
                :maxlength="128"
                :placeholder="__('Pick something nice')"
                @input="changePassword"
              />
            </div>
            <div v-if="visitorsCanModerate && requireApproval" class="tv-cell tv-cell-subtitle warning-scheme">
              <base-icon class="caption-type" name="warning" />
              <div>
                <strong> {{ __('"Require approval" is turned on.') }} </strong>
                <span>
                  {{
                    __(
                      'For the "Require approval" setting to have an effect, please set visitors’ permission to "Can write".',
                    )
                  }}
                </span>
              </div>
            </div>
            <div v-if="xPermissions" class="list-item">
              <div class="list-item-primary">{{ __('Visitor permissions') }}</div>
              <div class="list-item-secondary">
                <surface-panel-share-permissions-selector
                  :button-label="__('Open visitor permissions selector')"
                  :permission="right"
                  :options="permissionOptions"
                  @select="selectRight"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { __ } from 'bits/intl'
import { sanitizeAndCamelCaseTitle } from 'bits/sanitize_and_transform_text'
import {
  getWallAccessPrivacyTypeName,
  getWallAccessPrivacyTypeText,
  WALL_ACCESS_PRIVACY_TYPE_ICON,
} from 'bits/wall_access_settings_helper'
import { WallAccessPrivacyType, WallAccessRight } from 'enums'
import OzToggle from 'library/v4/components/OzToggle.vue'
import BaseIcon from 'vuecomponents/base_icon'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate.vue'
import SurfacePanelSharePermissionsSelector from 'vuecomponents/surface_panel_share_permissions_selector'
import { mapActions, mapGetters } from 'vuex'
import { ShareSubpanel } from 'vuexstore/modules/share'

export default {
  components: {
    BaseIcon,
    OzToggle,
    ProgressIndeterminate,
    SurfacePanelSharePermissionsSelector,
  },
  data() {
    return {
      WALL_ACCESS_PRIVACY_TYPE_ICON,
    }
  },
  computed: {
    ...mapGetters('wallAccessSettings', [
      'errorMessage',
      'areBaseWallAccessSettingsDirty',
      'isPasswordSet',
      'isSaving',
      'password',
      'privacyType',
      'right',
      'visitorsCanModerate',
    ]),
    ...mapGetters([
      'libraryType',
      'isSchoolLibrary',
      'isClassroomLibrary',
      'isLibraryWall',
      'isOrgWall',
      'organizationName',
      'requireApproval',
      'canIMakePrivateWalls',
      'canIMakeOrgWideDashboardWalls',
      'canIMakeOrgWideHiddenWalls',
      'canIMakeSecretWalls',
      'canIMakePasswordWalls',
      'canIMakeLibraryWideDashboardWalls',
      'canIMakeLibraryWideHiddenWalls',
      'canIMakeSecretLoggedInVisitorWalls',
      'canIMakePublicWalls',
    ]),
    canITurnOnDisplayInOrgDashboardToggle() {
      return this.canIMakeOrgWideDashboardWalls
    },
    canITurnOffDisplayInOrgDashboardToggle() {
      return this.canIMakeOrgWideHiddenWalls
    },
    canITurnOnDisplayInLibraryDashboardToggle() {
      return this.canIMakeLibraryWideDashboardWalls
    },
    canITurnOffDisplayInLibraryDashboardToggle() {
      return this.canIMakeLibraryWideHiddenWalls
    },
    canITurnOnSecretLoggedInVisitorsOnlyToggle() {
      return this.canIMakeSecretLoggedInVisitorWalls
    },
    canITurnOffSecretLoggedInVisitorsOnlyToggle() {
      return this.canIMakeSecretWalls
    },
    privacyOptions() {
      if (this.isOrgWall) {
        return [
          {
            type: WallAccessPrivacyType.Private,
            isEnabled: this.canIMakePrivateWalls,
          },
          {
            type: WallAccessPrivacyType.OrgWideUnlisted,
            isEnabled: this.canIMakeOrgWideDashboardWalls || this.canIMakeOrgWideHiddenWalls,
          },
          {
            type: WallAccessPrivacyType.PasswordProtected,
            isEnabled: this.canIMakePasswordWalls,
          },
          {
            type: WallAccessPrivacyType.Secret,
            isEnabled: this.canIMakeSecretWalls,
          },
        ]
      }
      if (this.isLibraryWall) {
        return [
          { type: WallAccessPrivacyType.Private, isEnabled: this.canIMakePrivateWalls },
          {
            type: WallAccessPrivacyType.LibraryWideUnlisted,
            isEnabled: this.canIMakeLibraryWideHiddenWalls || this.canIMakeLibraryWideDashboardWalls,
          },
          { type: WallAccessPrivacyType.PasswordProtected, isEnabled: this.canIMakePasswordWalls },
          {
            type: WallAccessPrivacyType.Secret,
            isEnabled: this.canIMakeSecretWalls || this.canIMakeSecretLoggedInVisitorWalls,
          },
          { type: WallAccessPrivacyType.Public, isEnabled: this.canIMakePublicWalls },
        ]
      }
      return [
        { type: WallAccessPrivacyType.Private, isEnabled: true },
        { type: WallAccessPrivacyType.PasswordProtected, isEnabled: true },
        { type: WallAccessPrivacyType.Secret, isEnabled: true },
        { type: WallAccessPrivacyType.Public, isEnabled: true },
      ]
    },
    privacyText() {
      return getWallAccessPrivacyTypeText({ organization: this.organizationName })
    },
    privacyTypeShortName() {
      return getWallAccessPrivacyTypeName({ libraryType: this.libraryType, length: 'short' })
    },
    permissionOptions() {
      const permissions = [WallAccessRight.Read, WallAccessRight.Write, WallAccessRight.Edit]
      if (
        this.privacyType === WallAccessPrivacyType.OrgWideUnlisted ||
        this.privacyType === WallAccessPrivacyType.OrgWideListed ||
        this.privacyType === WallAccessPrivacyType.LibraryWideUnlisted ||
        this.privacyType === WallAccessPrivacyType.LibraryWideListed
      ) {
        permissions.push(WallAccessRight.Administer)
      }
      return permissions
    },
    xPassword() {
      return this.privacyType === WallAccessPrivacyType.PasswordProtected
    },
    xLoggedInVisitorsToggle() {
      return !this.isOrgWall && (this.isPrivacyOptionSecret || this.isPrivacyOptionMembersOnly)
    },
    xDisplayInLibraryDashboardToggle() {
      return this.isPrivacyOptionLibraryWide
    },
    displayInLibraryDashboardToggleText() {
      if (this.isSchoolLibrary) return __('Display in school dashboard')
      if (this.isClassroomLibrary) return __('Display in classroom dashboard')
      return __('Display in team dashboard')
    },
    xDisplayInOrgDashboardToggle() {
      return this.isPrivacyOptionOrgWide
    },
    xPermissions() {
      return this.privacyType !== WallAccessPrivacyType.Private
    },
    xOptions() {
      return this.xPassword || this.xPermissions
    },
    isPrivacyOptionMembersOnly() {
      return this.privacyType === WallAccessPrivacyType.LoggedInUsersOnly
    },
    isPrivacyOptionLibraryWide() {
      return this.isPrivacyOptionLibraryWideUnlisted || this.isPrivacyOptionLibraryWideListed
    },
    isPrivacyOptionLibraryWideUnlisted() {
      return this.privacyType === WallAccessPrivacyType.LibraryWideUnlisted
    },
    isPrivacyOptionLibraryWideListed() {
      return this.privacyType === WallAccessPrivacyType.LibraryWideListed
    },
    isPrivacyOptionSecret() {
      return this.privacyType === WallAccessPrivacyType.Secret
    },
    isPrivacyOptionSecretLoggedInVisitors() {
      return this.privacyType === WallAccessPrivacyType.LoggedInUsersOnly
    },
    isPrivacyOptionOrgWide() {
      return this.isPrivacyOptionOrgWideUnlisted || this.isPrivacyOptionOrgWideListed
    },
    isPrivacyOptionOrgWideUnlisted() {
      return this.privacyType === WallAccessPrivacyType.OrgWideUnlisted
    },
    isPrivacyOptionOrgWideListed() {
      return this.privacyType === WallAccessPrivacyType.OrgWideListed
    },
  },
  methods: {
    ...mapActions('wallAccessSettings', ['setPrivacyType', 'setPassword', 'setRight', 'updateWallAccessSettings']),
    ...mapActions('share', ['confirmChangePanelDiscardChanges']),
    goBackToMainPanel() {
      this.confirmChangePanelDiscardChanges(ShareSubpanel.Main)
    },
    selectPrivacyType(privacyType) {
      const privacyOption = this.privacyOptions.find((option) => option.type === privacyType)
      if (this.isSelected(privacyType) || !privacyOption.isEnabled) return
      this.setPrivacyType({ privacyType })
    },
    changePassword(e) {
      const password = e.currentTarget.value
      this.setPassword({ password })
    },
    toggleLoggedInVisitorsOnly(loggedInVisitorsOnly) {
      if (!loggedInVisitorsOnly && this.canITurnOnSecretLoggedInVisitorsOnlyToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.LoggedInUsersOnly,
        })
      }

      if (loggedInVisitorsOnly && this.canITurnOffSecretLoggedInVisitorsOnlyToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.Secret,
        })
      }
    },
    toggleDisplayInTeamDashboard(displayInTeamDashboard) {
      if (!displayInTeamDashboard && this.canITurnOnDisplayInLibraryDashboardToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.LibraryWideListed,
        })
      }

      if (displayInTeamDashboard && this.canITurnOffDisplayInLibraryDashboardToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.LibraryWideUnlisted,
        })
      }
    },
    toggleDisplayInOrgDashboard(displayInOrgDashboard) {
      // Turn on "Display in org dashboard"
      if (!displayInOrgDashboard && this.canITurnOnDisplayInOrgDashboardToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.OrgWideListed,
        })
      }

      // Turn off "Display in org dashboard"
      if (displayInOrgDashboard && this.canITurnOffDisplayInOrgDashboardToggle) {
        this.setPrivacyType({
          privacyType: WallAccessPrivacyType.OrgWideUnlisted,
        })
      }
    },
    selectRight(right) {
      this.setRight({ right })
    },
    isSelected(key) {
      if (key === WallAccessPrivacyType.Secret) {
        return this.isPrivacyOptionSecret || this.isPrivacyOptionMembersOnly
      }
      if (key === WallAccessPrivacyType.LibraryWideUnlisted) {
        return this.isPrivacyOptionLibraryWide
      }
      if (key === WallAccessPrivacyType.OrgWideUnlisted) {
        return this.isPrivacyOptionOrgWide
      }
      return key === this.privacyType
    },
    save() {
      this.updateWallAccessSettings()
    },
    sanitizeAndCamelCaseTitle,
  },
}
</script>

<style lang="scss" scoped>
.privacy-option {
  .list-item-primary {
    align-items: center;
  }

  .description {
    margin-bottom: 4px;
  }
}

.privacy-option-settings .list-item {
  justify-content: space-between;
}

.privacy-password {
  text-align: right;
}
</style>
